import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import LoginPage from "@/pages/LoginPage";
import ConfiguratorPage from "@/pages/ConfiguratorPage";
import AdminPanel from "@/pages/AdminPanel";
import Dashboard from "@/pages/Dashboard";
import FullHeightTurnstiles from "@/pages/FullHeightTurnstiles";
import HalfHeightTurnstiles from "@/pages/HalfHeightTurnstiles";
import PedestrianEntranceControl from "@/pages/PedestrianEntranceControl";
import AccessControl from "@/pages/AccessControl";
import SavedQuotes from "@/pages/SavedQuotes";
import InviteTeam from "@/pages/InviteTeam";
import Reports from "@/pages/Reports";
import AtlasAccessControl from "@/pages/AtlasAccessControl";
import BioCVSecurity from "@/pages/BioCVSecurity";
import Securest from "@/pages/Securest";
import BioCVLanding from "@/pages/BioCVLanding";
import VisitorManagement from "@/pages/VisitorManagement";
import SoftwareVisitorManagement from "@/pages/SoftwareVisitorManagement";
import ParkingBarriers from "@/pages/ParkingBarriers";
import ParkingBarrierSystems from "@/pages/ParkingBarrierSystems";
import AdvancedParkingSolutions from "@/pages/AdvancedParkingSolutions";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AccountSettings from "@/pages/AccountSettings";
import DatasheetManagement from "@/pages/DatasheetManagement";
import DistributorDiscounts from "@/pages/DistributorDiscounts";
import ProductDiscountCaps from "@/pages/ProductDiscountCaps";
import PricingManagement from "@/pages/PricingManagement";
import HelpGuide from "@/pages/HelpGuide";
import SpaceConfigurator from "@/pages/SpaceConfigurator";
import SalesDesk from "@/salesdesk/SalesDesk";
import { isDisabledPath, DISABLED_MODULES } from "@/config/disabledModules";
import { ModuleUnavailable } from "@/components/ModuleUnavailable";
import PublicDatasheets from "@/pages/PublicDatasheets";
// Amazon Portal imports
import AmazonLoginPage from "@/pages/AmazonLoginPage";
import AmazonDashboard from "@/pages/AmazonDashboard";
import AmazonHardeningProject from "@/pages/AmazonHardeningProject";
import AmazonSavedQuotes from "@/pages/AmazonSavedQuotes";
import AmazonProductsDatasheets from "@/pages/AmazonProductsDatasheets";
import AmazonInviteTeam from "@/pages/AmazonInviteTeam";
import AmazonAdminPanel from "@/pages/AmazonAdminPanel";
import PriceListConverter from "@/pages/PriceListConverter";
import PartsLookup from "@/pages/PartsLookup";
import CustomerFeedback from "@/pages/CustomerFeedback";
import FeedbackResponses from "@/pages/FeedbackResponses";
import BroadcastEmail from "@/pages/BroadcastEmail";
import ResourceViewer from "@/pages/ResourceViewer";
import ResourceManager from "@/pages/ResourceManager";
import VideoResources from "@/pages/VideoResources";
import LandingHub from "@/pages/LandingHub";
import ZKTecoPresentation from "@/pages/ZKTecoPresentation";
import PresentationResource from "@/pages/PresentationResource";
import PresentationViewer from "@/pages/PresentationViewer";
import { Button } from "@/components/ui/button";
import { Calculator, LogOut, Settings, ArrowLeft } from "lucide-react";
import { useVersionCheck } from "@/hooks/useVersionCheck";

// Main app content wrapper with router access
function AppContent() {
  const [user, setUser] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const navigate = useNavigate();
  
  // Check for app version updates and auto-refresh if needed
  useVersionCheck();

  useEffect(() => {
    // Force fresh login - clear any existing tokens on app load ONLY
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Setup axios interceptor to catch auth errors globally
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          // Check if user was logged in by checking if token exists
          const hadToken = localStorage.getItem('token') !== null;
          
          // Don't show alert for login endpoint failures (those are handled by LoginPage)
          const isLoginEndpoint = error.config?.url?.includes('/auth/login');
          
          // Auto logout on unauthorized errors
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          
          // Only alert if:
          // 1. User had an active session (token existed)
          // 2. It's not a login attempt failure
          if (hadToken && !isLoginEndpoint) {
            alert('Your session has expired. Please log in again.');
          }
        }
        return Promise.reject(error);
      }
    );
    
    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []); // Run only once on mount

  const handleLogin = (userData) => {
    setUser(userData);
    const path = window.location.pathname;
    // Redirect Amazon users to Amazon Dashboard
    if (userData?.role === 'amazon' || userData?.role === 'amazon_admin') {
      navigate('/amazon/dashboard');
    } else if (userData?.role === 'securest') {
      navigate('/securest');
    } else if (path.toLowerCase().startsWith('/ai-salesdesk') || path.toLowerCase().startsWith('/securest')) {
      // Preserve direct-URL access to the hidden AI Sales Desk and the Securest module
      navigate(path);
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const handleLoadQuote = (quote) => {
    setSelectedQuote(quote);
    // Route to the correct configurator based on type
    if (quote.configurator_type === 'parking_barrier') {
      navigate('/parking-barriers');
    } else if (quote.configurator_type === 'full_height_turnstiles') {
      navigate('/full-height-turnstiles');
    } else if (quote.configurator_type === 'atlas') {
      navigate('/atlas-access');
    } else {
      // Default to Comet Speedlane configurator
      navigate('/configurator');
    }
  };

  // Switched-off modules (Amazon, AI Sales Desk, presentations) — blocked for everyone
  if (isDisabledPath(window.location.pathname)) {
    return <ModuleUnavailable />;
  }

  // Handle Amazon Portal routes FIRST (before main auth check)
  // Amazon has its own authentication system
  const isAmazonRoute = window.location.pathname.startsWith('/amazon');
  if (isAmazonRoute) {
    return (
      <Routes>
        <Route path="/amazon" element={<AmazonRoutes user={user} setUser={setUser} />} />
        <Route path="/amazon/*" element={<AmazonRoutes user={user} setUser={setUser} />} />
      </Routes>
    );
  }

  // Public feedback route - no auth required, bypass login entirely
  const isFeedbackRoute = window.location.pathname === '/customer-satisfaction-feedback';
  if (isFeedbackRoute) {
    return (
      <Routes>
        <Route path="/customer-satisfaction-feedback" element={<CustomerFeedback />} />
      </Routes>
    );
  }

  // Public resource viewer - no auth required
  const isResourceRoute = window.location.pathname.startsWith('/resource/');
  if (isResourceRoute) {
    return (
      <Routes>
        <Route path="/resource/:id" element={<ResourceViewer />} />
      </Routes>
    );
  }

  // Public BioCV Available Modules page - no auth required
  const isBioCVLandingRoute = window.location.pathname === '/bio-cvsecurity/modules';
  if (isBioCVLandingRoute) {
    return (
      <Routes>
        <Route path="/bio-cvsecurity/modules" element={<BioCVLanding onBack={() => window.location.href = '/'} />} />
      </Routes>
    );
  }

  // Public custom presentation viewer - no auth required
  const isPresentationViewRoute = window.location.pathname.startsWith('/presentation/view/');
  if (isPresentationViewRoute) {
    return (
      <Routes>
        <Route path="/presentation/view/:id" element={<PresentationViewer />} />
      </Routes>
    );
  }

  // If Amazon user tries to access main dashboard, log them out
  if (user && (user.role === 'amazon' || user.role === 'amazon_admin')) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
      </Routes>
    );
  }

  // If not logged in, show login page or reset password page
  if (!user) {
    return (
      <Routes>
        <Route path="/customer-satisfaction-feedback" element={<CustomerFeedback />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<LoginPage onLogin={handleLogin} />} />
      </Routes>
    );
  }

  // Securest-only accounts: lock them to the Securest module
  if (user.role === 'securest') {
    return (
      <Routes>
        <Route path="/securest" element={<Securest onLogout={handleLogout} />} />
        <Route path="*" element={<Navigate to="/securest" replace />} />
      </Routes>
    );
  }

  // If logged in, show main app with routes
  return (
    <div className="App">
      <Routes>
        {/* Public routes - no login required */}
        <Route path="/datasheets" element={<PublicDatasheets />} />
        <Route path="/biocv-datasheets" element={<PublicDatasheets />} />
        <Route path="/customer-satisfaction-feedback" element={<CustomerFeedback />} />
        
        {/* Protected routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/home" element={<Navigate to="/dashboard" replace />} />
        <Route path="/presentation" element={
          <ZKTecoPresentation onBack={() => navigate('/presentation-resource')} />
        } />
        <Route path="/presentation-resource" element={
          <PresentationResource onBack={() => navigate('/dashboard')} />
        } />
        <Route path="/presentation/view/:id" element={<PresentationViewer />} />
        <Route path="/video-resources" element={
          <Navigate to="/presentation-resource" replace />
        } />
        <Route path="/dashboard" element={
          <Dashboard 
            onNavigate={(view) => navigate(`/${view}`)}
            user={user}
            onLogout={handleLogout}
          />
        } />
        <Route path="/Securest" element={<Securest onBack={() => navigate('/dashboard')} />} />
        <Route path="/securest" element={<Securest onBack={() => navigate('/dashboard')} />} />
        <Route path="/configurator" element={
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <ConfiguratorPage 
              selectedQuote={selectedQuote}
              onBack={() => {
                const goTo = selectedQuote ? '/saved-quotes' : '/pedestrian-entrance-control';
                setSelectedQuote(null);
                navigate(goTo);
              }}
              backLabel={selectedQuote ? "Back to Saved Quotes" : "Back to Pedestrian Entrance Control"}
            />
          </div>
        } />
        <Route path="/pedestrian-entrance-control" element={
          <PedestrianEntranceControl onBack={() => navigate('/dashboard')} />
        } />
        <Route path="/full-height-turnstiles" element={
          <FullHeightTurnstiles 
            onBack={() => {
              const goTo = selectedQuote ? '/saved-quotes' : '/pedestrian-entrance-control';
              setSelectedQuote(null);
              navigate(goTo);
            }} 
            selectedQuote={selectedQuote}
            backLabel={selectedQuote ? "Back to Saved Quotes" : "Back to Pedestrian Entrance Control"}
          />
        } />
        <Route path="/half-height-turnstiles" element={
          <HalfHeightTurnstiles onBack={() => navigate('/pedestrian-entrance-control')} />
        } />
        <Route path="/space-configurator" element={
          <SpaceConfigurator onBack={() => navigate('/pedestrian-entrance-control')} />
        } />
        <Route path="/access-control" element={
          <AccessControl onBack={() => navigate('/dashboard')} />
        } />
        <Route path="/atlas-access" element={
          <AtlasAccessControl 
            onBack={() => {
              const goTo = selectedQuote ? '/saved-quotes' : '/access-control';
              setSelectedQuote(null);
              navigate(goTo);
            }} 
            selectedQuote={selectedQuote}
            backLabel={selectedQuote ? "Back to Saved Quotes" : "Back to Access Control"}
          />
        } />
        <Route path="/bio-cvsecurity" element={
          <BioCVLanding 
            onBack={() => navigate('/access-control')} 
          />
        } />
        <Route path="/bio-cvsecurity-config" element={
          <BioCVSecurity 
            onBack={() => navigate('/bio-cvsecurity')} 
            backLabel="Back to Bio CVSecurity"
          />
        } />
        <Route path="/visitor-management" element={
          <VisitorManagement onBack={() => navigate('/access-control')} />
        } />
        <Route path="/software-visitor-management" element={
          <SoftwareVisitorManagement onBack={() => navigate('/visitor-management')} />
        } />
        <Route path="/parking-barriers" element={
          <ParkingBarriers 
            onBack={() => {
              setSelectedQuote(null);
              navigate('/parking-barrier-systems');
            }} 
            selectedQuote={selectedQuote}
          />
        } />
        <Route path="/parking-barrier-systems" element={
          <ParkingBarrierSystems onBack={() => navigate('/dashboard')} />
        } />
        <Route path="/advanced-parking" element={
          <AdvancedParkingSolutions onBack={() => navigate('/parking-barrier-systems')} />
        } />
        <Route path="/saved-quotes" element={
          <SavedQuotes 
            onBack={() => navigate('/dashboard')}
            onLoadQuote={handleLoadQuote}
            user={user}
          />
        } />
        <Route path="/invite-team" element={
          <InviteTeam onBack={() => navigate('/dashboard')} user={user} />
        } />
        <Route path="/admin" element={
          user && (user.role === 'admin' || user.role === 'zkteco_admin' || user.role === 'distributor_admin') ? (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <AdminPanel user={user} />
            </div>
          ) : (
            <Navigate to="/dashboard" replace />
          )
        } />
        <Route path="/datasheet-management" element={
          <DatasheetManagement onBack={() => navigate('/dashboard')} />
        } />
        <Route path="/distributor-discounts" element={
          <DistributorDiscounts user={user} />
        } />
        <Route path="/product-discount-caps" element={
          <ProductDiscountCaps user={user} />
        } />
        <Route path="/pricing-management" element={
          <PricingManagement user={user} />
        } />
        <Route path="/parts-lookup" element={
          <PartsLookup onBack={() => navigate('/dashboard')} />
        } />
        <Route path="/reports" element={<Reports />} />
        <Route path="/AI-Salesdesk/*" element={<SalesDesk user={user} />} />
        <Route path="/feedback-responses" element={
          user && (user.role === 'admin' || user.role === 'zkteco_admin') ? (
            <FeedbackResponses onBack={() => navigate('/dashboard')} />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        } />
        <Route path="/broadcast-email" element={
          user && user.email === 'adam.jagger@zkteco.co.uk' ? (
            <BroadcastEmail onBack={() => navigate('/dashboard')} />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        } />
        <Route path="/resource-manager" element={
          user && user.email === 'adam.jagger@zkteco.co.uk' ? (
            <ResourceManager onBack={() => navigate('/dashboard')} />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        } />
        <Route path="/video-resources" element={
          <VideoResources onBack={() => navigate('/dashboard')} />
        } />
        <Route path="/resource/:id" element={<ResourceViewer />} />
        <Route path="/account-settings" element={<AccountSettings />} />
        <Route path="/help" element={<HelpGuide />} />
        <Route path="/pricelist-converter" element={
          user && (user.role === 'admin' || user.role === 'zkteco_admin' || user.role === 'zkteco') ? (
            <PriceListConverter onBack={() => navigate('/dashboard')} />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        } />
        
        {/* Amazon Portal Routes */}
        <Route path="/amazon" element={
          <AmazonRoutes user={user} setUser={setUser} />
        } />
        <Route path="/amazon/*" element={
          <AmazonRoutes user={user} setUser={setUser} />
        } />
      </Routes>
    </div>
  );
}

// Amazon Portal Routes Component
function AmazonRoutes({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user is an Amazon user or admin viewing Amazon portal
  const isAmazonUser = user?.role === 'amazon' || user?.role === 'amazon_admin';
  const isMainAdmin = user?.role === 'admin' || user?.role === 'zkteco' || user?.role === 'zkteco_admin';
  const canAccessAmazon = isAmazonUser || isMainAdmin;
  
  const handleAmazonLogin = (userData) => {
    setUser(userData);
    navigate('/amazon/dashboard');
  };
  
  const handleAmazonLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/amazon');
  };

  // If not logged in or not authorized, show Amazon login
  if (!user || !canAccessAmazon) {
    return <AmazonLoginPage onLogin={handleAmazonLogin} />;
  }

  // Amazon authenticated routes
  const path = location.pathname.replace('/amazon', '') || '/';
  
  switch (path) {
    case '/':
    case '/dashboard':
      return <AmazonDashboard user={user} onLogout={handleAmazonLogout} />;
    case '/hardening-project':
      return <AmazonHardeningProject onBack={() => navigate('/amazon/dashboard')} />;
    case '/saved-quotes':
      return <AmazonSavedQuotes onBack={() => navigate('/amazon/dashboard')} />;
    case '/products-datasheets':
      return <AmazonProductsDatasheets onBack={() => navigate('/amazon/dashboard')} />;
    case '/datasheets':
      return <Navigate to="/amazon/products-datasheets" replace />;
    case '/products':
      return <Navigate to="/amazon/products-datasheets" replace />;
    case '/invite-team':
      return <AmazonInviteTeam onBack={() => navigate('/amazon/dashboard')} />;
    case '/admin':
      if (isAmazonUser && user?.role !== 'amazon_admin' && !isMainAdmin) {
        return <Navigate to="/amazon/dashboard" replace />;
      }
      return <AmazonAdminPanel onBack={() => navigate('/amazon/dashboard')} user={user} />;
    default:
      return <Navigate to="/amazon/dashboard" replace />;
  }
}

// Wrap AppContent with BrowserRouter
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
